import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MapaAtasResponse } from "../../../../types/database";
import * as customizationService from "../../../services/customization.service";

interface MapaAtasPreviewProps {
  data: MapaAtasResponse;
}

export function MapaAtasPreview({ data }: MapaAtasPreviewProps) {
  const { t, i18n } = useTranslation('common');
  const { projeto, atas, topicos, itensSemTopico } = data;
  const [headerImageUrl, setHeaderImageUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchCustomization = async () => {
      try {
        const customization = await customizationService.getUserCustomization();
        setHeaderImageUrl(customization.header_image_url);
      } catch (error) {
        console.error("Erro ao carregar personalização:", error);
      }
    };

    fetchCustomization();
  }, []);


  const calcularZoom = () => {
    const numAtas = atas.length;
    if (numAtas <= 3) return 0.95;
    if (numAtas <= 5) return 0.85;
    if (numAtas <= 7) return 0.75;
    if (numAtas <= 10) return 0.65;
    if (numAtas <= 15) return 0.55;
    if (numAtas <= 20) return 0.45;
    return 0.35;
  };

  const zoomDinamico = calcularZoom();

  return (
    <div className="p-4 bg-white print-mapa-atas">
      <style>{`
        @media print {
          body {
            zoom: ${zoomDinamico};
          }
          
          .print-mapa-atas table {
            width: 100% !important;
            table-layout: auto !important;
          }
          
          .print-mapa-atas th,
          .print-mapa-atas td {
            white-space: nowrap !important;
          }
          
          .print-mapa-atas td:nth-child(2) {
            white-space: normal !important;
            word-wrap: break-word !important;
            max-width: 200pt !important;
          }

          .print-mapa-atas img {
            display: block !important;
            max-height: 60pt !important;
            max-width: 200pt !important;
          }
        }
      `}</style>
      
      {/* Header */}
      <div className="mb-4 border-b-2 border-gray-800 pb-2">
        <div className="flex justify-between items-start">
          <div className="flex-1 text-center">
            <h1 className="text-xl font-bold text-gray-900 mb-1">
              {t('mapaAtas.title').toUpperCase()}
            </h1>
            <h2 className="text-lg font-semibold text-gray-800">
              {projeto.nome}
            </h2>
            {projeto.endereco && (
              <p className="text-xs text-gray-600 mt-1">{projeto.endereco}</p>
            )}
          </div>

          {}
          {headerImageUrl && (
            <div className="ml-4">
              <img
                src={headerImageUrl}
                alt="Logo"
                className="h-16 w-auto max-w-[250px] object-contain"
              />
            </div>
          )}
        </div>
      </div>

      {}
      <div className="overflow-visible">
        <table className="w-full border-collapse text-xs" style={{ width: '100%', tableLayout: 'auto' }}>
          <thead>
            <tr className="bg-gray-200">
              <th className="border-2 border-gray-800 px-2 py-1.5 text-left font-bold" style={{ width: 'auto', minWidth: '60px' }}>
                {t('mapaAtas.code')}
              </th>
              <th className="border-2 border-gray-800 px-2 py-1.5 text-left font-bold" style={{ width: 'auto', minWidth: '200px' }}>
                {t('mapaAtas.description')}
              </th>
              {atas.map((ata) => (
                <th
                  key={ata.numero}
                  className="border-2 border-gray-800 px-1.5 py-1.5 text-center font-bold"
                  style={{ width: 'auto', minWidth: '50px' }}
                >
                  <div className="text-[9px] leading-tight whitespace-nowrap">{t('mapaAtas.meeting')}</div>
                  <div className="text-xs font-bold">{ata.numero}</div>
                  <div className="text-[9px] leading-tight mt-0.5 whitespace-nowrap">
                    {ata.data}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
        <tbody>
          {}
          {topicos.map((topico) => {
            const pendingItens = topico.itens.filter(item => !item.concluido);
            if (pendingItens.length === 0) return null;
            return (
            <React.Fragment key={topico.id}>
              {}
              <tr className="bg-gray-100">
                <td
                  colSpan={2 + atas.length}
                  className="border-2 border-gray-800 px-2 py-1.5 font-bold text-gray-900"
                >
                  {topico.codigo}. {topico.titulo}
                </td>
              </tr>
              {}
              {pendingItens.map((item) => (
                <tr key={item.id} className="page-break-inside-avoid">
                  <td className="border border-gray-600 px-2 py-1.5 font-mono text-[10px] font-semibold">
                    {item.codigo}
                  </td>
                  <td className="border border-gray-600 px-2 py-1.5 text-[10px] break-words">
                    {item.descricao}
                  </td>
                  {atas.map((ata) => (
                    <td
                      key={ata.numero}
                      className="border border-gray-600 text-center p-1.5"
                    >
                      {item.presencas.includes(ata.numero) && (
                        <div className="w-2.5 h-2.5 bg-black rounded-full mx-auto print:bg-black" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          );
          })}

          {}
          {itensSemTopico.filter(item => !item.concluido).length > 0 && (
            <>
              <tr className="bg-gray-100">
                <td
                  colSpan={2 + atas.length}
                  className="border-2 border-gray-800 px-2 py-1.5 font-bold text-gray-900"
                >
                  {t('mapaAtas.items_without_topic')}
                </td>
              </tr>
              {itensSemTopico.filter(item => !item.concluido).map((item) => (
                <tr key={item.id} className="page-break-inside-avoid">
                  <td className="border border-gray-600 px-2 py-1.5 font-mono text-[10px] font-semibold">
                    {item.codigo}
                  </td>
                  <td className="border border-gray-600 px-2 py-1.5 text-[10px] break-words">
                    {item.descricao}
                  </td>
                  {atas.map((ata) => (
                    <td
                      key={ata.numero}
                      className="border border-gray-600 text-center p-1.5"
                    >
                      {item.presencas.includes(ata.numero) && (
                        <div className="w-2.5 h-2.5 bg-black rounded-full mx-auto print:bg-black" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
      </div>

      {}
      <div className="mt-3 text-[10px] text-gray-600 border-t border-gray-400 pt-2">
        <div className="flex justify-between">
          <div>
            <strong>Legenda:</strong> ● = Item presente na ata
          </div>
          <div>
            Gerado em: {new Date().toLocaleString(i18n.language, {
              day: "2-digit", month: "2-digit", year: "numeric",
              hour: "2-digit", minute: "2-digit",
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
